import {useScopeQuery} from "@/core/store/scopeQuery";
import {
    BasketRule,
    BasketRuleBenefitDiscount,
    BasketRuleBenefitGift,
    BasketRuleBenefitInterface,
    BenefitTypeEnum
} from "@/gql/gen";
import {ProductModel} from "@/modules/shop/composable/useProduct";
import {useBasketStore} from "@/modules/shop/store/basket";
import {useCatalogStore} from "@/modules/shop/store/catalog";
import {useLoalityStore} from "@/modules/shop/store/loyalty";
import {RecalculateContext} from "@/modules/shop/store/types/orchestrator";
import {BasketItemCalculated, ProductDiscountsCalculation} from "@/modules/shop/store/util/benefit";
import {calculateProductDiscountsForCatalogUtil, calculateProductDiscountsUtil} from "@/modules/shop/store/util/vorder";
import {useVorderCoupon} from "@/modules/shop/store/vorder-coupon";
import {useVorderPrize} from "@/modules/shop/store/vorder-prize";
import {useVorderState} from "@/modules/shop/store/vorder-state";
import {checkOrderRestriction, RestrictionCheckResult} from "@/modules/shop/util/checkOrderRestriction";
import {filterOrderCondition} from "@/modules/shop/util/orderConditionFilter";
import debounce from "lodash-es/debounce";
import {defineStore, storeToRefs} from "pinia";
import {computed, ref, watch} from "vue";
import {useDeliveryStore} from "@/modules/shop/store/delivery";

const STORE_NAME = "basket-rules";

/**
 * Универсальный benefit с информацией о правиле
 */
export type BenefitWithRule = BasketRuleBenefitInterface & {
    rule: BasketRule;
    ruleId: number;
};

export type GiftBenefitWithRule = BasketRuleBenefitGift & {
    rule: BasketRule;
    ruleId: number;
};

export type DiscountBenefitWithRule = BasketRuleBenefitDiscount & {
    rule: BasketRule;
    ruleId: number;
};

/**
 * Состояние правила корзины
 */
export interface BasketRuleState {
    rule: BasketRule;

    baseActive: boolean;
    baseActiveCheckResult: RestrictionCheckResult | null

    active: boolean;
    activeCheckResult: RestrictionCheckResult | null

    progress: number;              // Прогресс активации (0-1) для ORDER_PRICE_FROM
    priceRemaining: number;        // Сколько не хватает до ORDER_PRICE_FROM
    benefits: BenefitWithRule[];  // Benefits с привязкой к правилу
}

/**
 * Pinia store для работы с правилами корзины (BasketRules)
 *
 * Система BasketRules - унифицированная платформа для управления:
 * - Скидками
 * - Подарками
 * - Промокодами
 * - Призами
 *
 * Логика выбора подарков:
 * - Если по одному правилу несколько подарков → можно выбрать только один из них
 * - Если активно несколько правил с подарками → можно выбрать только один подарок из всех правил
 * - Исключение: правило с IS_ADDITIONAL = true → один дополнительный подарок независимо от других
 * - Проверка CONDITION.BENEFIT_TYPE должна соответствовать attrs.BENEFIT_TYPE
 */
export const useBasketRulesStore = defineStore(STORE_NAME, () => {
    // Получаем необходимые stores и утилиты
    const basketStore = useBasketStore();
    const vorderState = useVorderState();
    const catalogStore = useCatalogStore();
    const deliveryStore = useDeliveryStore();
    const vorderPrize = useVorderPrize();
    const vorderCoupon = useVorderCoupon();
    const loalityStore = useLoalityStore();

    const {registerScopeQuery} = useScopeQuery();

    // Извлекаем реактивные данные
    const {attrs, bonuses, benefitType} = storeToRefs(vorderState);
    const {productsModelsById} = storeToRefs(catalogStore);

    /**
     * Все правила корзины (загружаются с бэкенда через scope query)
     */
    const allRules = ref<BasketRule[]>([]);

    /**
     * Выбранный основной подарок (UID или PRODUCT_ID)
     */
    const selectedGift = ref<number | string | null>(null);

    /**
     * Выбранный дополнительный подарок (UID или PRODUCT_ID) для IS_ADDITIONAL правил
     */
    const selectedGiftAdditional = ref<number | string | null>(null);

    /**
     * Карта состояний всех правил
     * Обновляется через recalculateActiveBasketRules из оркестратора
     */
    const rulesStateMap = ref<Record<number, BasketRuleState>>({});


    /**
     * Функция для пересчета состояний правил
     * Вызывается из оркестратора (useVorderStore)
     *
     * Проверяет условия и вычисляет прогресс:
     * - ACTIVE === true
     * - Условия правила (через checkOrderRestriction)
     * - progress и priceRemaining для ORDER_PRICE_FROM
     *
     * @param context - Контекст с данными из оркестратора
     */
    const recalculateActiveBasketRules = (context: RecalculateContext = {}) => {
        // ВАЖНО: Контекст должен быть передан из оркестратора
        if (!context.restrictionContext) {
            console.warn('[BasketRulesStore] recalculateActiveBasketRules called without restrictionContext');
            return;
        }

        //console.log('[BasketRulesStore] recalculateActiveBasketRules called');
        //console.log('[BasketRulesStore] restrictionContext:', context.restrictionContext);

        const result: Record<number, BasketRuleState> = {};
        const basketTotal = context.basketPriceTotal ?? 0;

        // Обрабатываем все правила
        for (const rule of allRules.value) {

            const state: BasketRuleState = {
                rule,

                baseActive: false,
                baseActiveCheckResult: null,

                active: false,
                activeCheckResult: null,

                benefits: [],
                progress: 0,
                priceRemaining: 0,
            };

            // Шаг 1: Проверяем базовую активность правила
            if (!rule.ACTIVE) {
                state.baseActive = false
                state.active = false
            } else {
                const baseCondition = filterOrderCondition(rule.CONDITION, ['BENEFIT_TYPE', 'PRODUCT', 'PRODUCT_SECTION'])
                state.baseActiveCheckResult = checkOrderRestriction(baseCondition, context.restrictionContext);
                state.baseActive = state.baseActiveCheckResult.isApplicable

                const activeCondition = rule.CONDITION
                state.activeCheckResult = checkOrderRestriction(activeCondition, context.restrictionContext);
                state.active = state.activeCheckResult.isApplicable
            }

            if (state.active) {
                // Активное правило имеет прогресс 1
                state.progress = 1;
            } else if (rule.CONDITION?.ORDER_PRICE_FROM) {
                // Для неактивных правил с ORDER_PRICE_FROM вычисляем прогресс
                state.priceRemaining = rule.CONDITION.ORDER_PRICE_FROM - basketTotal;
                state.progress = Math.min(1, Math.max(0, basketTotal / rule.CONDITION.ORDER_PRICE_FROM));
            }

            // Собираем benefits с привязкой к правилу (расширяем каждый benefit)
            state.benefits = rule.BENEFITS
                ? rule.BENEFITS.map(benefit => ({
                    ...benefit,
                    rule,
                    ruleId: rule.ID,
                }))
                : [];

            result[rule.ID] = state
        }

        // Обновляем ref
        rulesStateMap.value = result;

        console.log('context.restrictionContext', context.restrictionContext)
        console.log('[BasketRulesStore] Updated rulesStateMap', result);
    };

    // Debounced версия для использования в оркестраторе
    const debouncedRecalculateActiveBasketRules = debounce(recalculateActiveBasketRules, 300);

    const rules = computed<BasketRuleState[]>(() => {
        return Object.values(rulesStateMap.value);
    });

    const activeRules = computed<BasketRuleState[]>(() => {
        return Object.values(rulesStateMap.value)
            .filter(state =>
                    state
                    && state.active
                    && (
                        !state.rule.CONDITION.BENEFIT_TYPE.length
                        || benefitType.value && state.rule.CONDITION.BENEFIT_TYPE.includes(benefitType.value)
                    )
            );
    });

    const baseActiveRules = computed<BasketRuleState[]>(() => {
        return Object.values(rulesStateMap.value).filter(state => state.baseActive);
    });

    const benefits = computed<BenefitWithRule[]>(() => {
        const benefits: BenefitWithRule[] = [];
        for (const state of rules.value) {
            benefits.push(...state.benefits);
        }
        return benefits;
    });

    const baseActiveBenefits = computed<BenefitWithRule[]>(() => {
        const benefits: BenefitWithRule[] = [];
        for (const state of rules.value) {
            if (state.baseActive) {
                benefits.push(...state.benefits);
            }
        }
        return benefits;
    });

    const activeBenefits = computed<BenefitWithRule[]>(() => {
        const benefits: BenefitWithRule[] = [];
        for (const state of activeRules.value) {
            benefits.push(...state.benefits);
        }
        return benefits;
    });

    const discountBenefitsStandalone = computed<DiscountBenefitWithRule[]>(() => {
        return baseActiveBenefits.value.filter(item => item.__typename === 'BasketRuleBenefitDiscount').filter(item => item.rule.CONDITION.BENEFIT_TYPE.includes('discount'));
    });

    const discountBenefitsStandaloneBest = computed(() => {
        return getMaxDiscount(discountBenefitsStandalone.value)
    });

    const pickupDiscount = computed(() => {
        return benefits.value
            .filter(item => item.__typename === 'BasketRuleBenefitDiscount')
            .find(item => item.rule.TYPE === 'discount' && item.rule.CONDITION.BENEFIT_TYPE.includes('discount') && item.rule.CONDITION.TRANSPORT_TYPE.includes('pickup'));
    });

    const promoBenefits = computed(() => {
        return benefits.value.filter(item => item.__typename === 'BasketRuleBenefitDiscount' && item.rule.PROMO)
    });

    /**
     * Скидка на доставку
     * Ищет активные правила скидок для доставки (TRANSPORT_TYPE = 'courier')
     */
    const deliveryDiscount = computed(() => {
        return activeBenefits.value
            .filter(item => item.__typename === 'BasketRuleBenefitDiscount')
            .find(item => item.rule.TYPE === 'discount' && item.rule.CONDITION.BENEFIT_TYPE.includes('discount') && item.rule.CONDITION.TRANSPORT_TYPE.includes('courier'));
    });

    /**
     * Стоимость доставки со скидкой
     * Применяет скидку из deliveryDiscount к deliveryPrice из deliveryStore
     */
    const deliveryPriceDiscounted = computed(() => {
        const basePrice = deliveryStore.deliveryPrice;

        if (!deliveryDiscount.value || !deliveryDiscount.value.DISCOUNT_PERCENT) {
            return basePrice;
        }

        const discountPercent = deliveryDiscount.value.DISCOUNT_PERCENT;
        const discountedPrice = basePrice * (1 - discountPercent / 100);

        return Math.max(0, Math.round(discountedPrice));
    });


    /**
     * Базово активные правила скидок (без учёта PRODUCT) - полное состояние
     * Для показа в каталоге
     */
    const baseActiveDiscountRules = computed<BasketRuleState[]>(() => {
        return baseActiveRules.value.filter(state => state.rule.TYPE === 'discount');
    });

    const catalogActiveDiscountRules = computed<BasketRuleState[]>(() => {
        return baseActiveRules.value.filter(state =>
                state.rule.TYPE === 'discount'
                && (
                    !state.rule.CONDITION.BENEFIT_TYPE.length
                    || benefitType.value && state.rule.CONDITION.BENEFIT_TYPE.includes(benefitType.value)
                )
        );
    });


    /**
     * Активные правила скидок - полное состояние
     */
    const activeDiscountRules = computed<BasketRuleState[]>(() => {
        return activeRules.value.filter(state => state.rule.TYPE === 'discount');
    });

    /**
     * Активные правила подарков - полное состояние
     */
    const activeGiftRules = computed<BasketRuleState[]>(() => {
        return activeRules.value.filter(state => state.rule.TYPE === 'gift');
    });

    /**
     * Активные правила подарков (обычные, не IS_ADDITIONAL) - полное состояние
     */
    const activeRegularGiftRules = computed<BasketRuleState[]>(() => {
        return activeGiftRules.value.filter(state => !state.rule.IS_ADDITIONAL);
    });

    /**
     * Активные правила дополнительных подарков (IS_ADDITIONAL = true) - полное состояние
     */
    const activeAdditionalGiftRules = computed<BasketRuleState[]>(() => {
        return activeGiftRules.value.filter(state => state.rule.IS_ADDITIONAL);
    });

    /**
     * Активные правила специальной цены (special) - полное состояние
     */
    const activeSpecialRules = computed<BasketRuleState[]>(() => {
        return activeRules.value.filter(state => state.rule.TYPE === 'special');
    });


    /**
     * Доступные скидки с информацией о правиле
     */
    const availableDiscounts = computed<DiscountBenefitWithRule[]>(() => {
        return activeBenefits.value.filter(item => item.__typename === 'BasketRuleBenefitDiscount');
    });

    /**
     * Доступные подарки (обычные) с информацией о правиле
     */
    const availableGiftsAll = computed<GiftBenefitWithRule[]>(() => {
        return activeBenefits.value.filter(item => item.__typename === 'BasketRuleBenefitGift');
    });

    /**
     * Доступные подарки (обычные) с информацией о правиле
     */
    const availableGifts = computed<GiftBenefitWithRule[]>(() => {
        return availableGiftsAll.value.filter(item => !item.rule.IS_ADDITIONAL)
    });

    /**
     * Доступные дополнительные подарки (IS_ADDITIONAL) с информацией о правиле
     */
    const availableAdditionalGifts = computed<GiftBenefitWithRule[]>(() => {
        return availableGiftsAll.value.filter(item => !item.rule.IS_ADDITIONAL)
    });

    /**
     * Товары доступные по special правилам
     * Используется для отображения товаров по специальной цене
     */
    const specialProducts = computed<ProductModel[]>(() => {
        return getProducts({
            ruleActive: true,
            benefitTypes: ['special'],
        });
    });

    /**
     * Проверка доступности товара по special правилам
     * @param productId - ID товара
     * @returns true если товар доступен по активным special правилам
     */
    const isProductAvailableBySpecial = (productId: number): boolean => {
        return specialProducts.value.some(p => p.ID === productId);
    };

    /**
     * Получить активные правила special для товара (полное состояние)
     * @param productId - ID товара
     * @returns Массив активных состояний правил special для данного товара
     */
    const getSpecialRulesStatesForProduct = (productId: number): BasketRuleState[] => {
        return activeSpecialRules.value.filter(state => {
            if (!state.rule.PROPERTIES?.BENEFITS_PRODUCT_ID_ENTITIES) return false;
            return state.rule.PROPERTIES.BENEFITS_PRODUCT_ID_ENTITIES.some(
                entity => entity.ID === productId
            );
        });
    };


    /**
     * Проверяет, доступен ли подарок в текущих активных правилах
     */
    const isGiftAvailable = (giftId: number | string, isAdditional = false): boolean => {
        const gifts = isAdditional ? availableAdditionalGifts.value : availableGifts.value;

        return gifts.some(gift => {
            return gift.UID === giftId || gift.PRODUCT_ID === giftId;
        });
    };

    /**
     * Находит лучший подарок на основе CONDITION.ORDER_PRICE_FROM
     */
    const findBestGift = (isAdditional = false): number | string | null => {
        const states = isAdditional ? activeAdditionalGiftRules.value : activeRegularGiftRules.value;

        if (states.length === 0) return null;

        // Сортируем правила по ORDER_PRICE_FROM (от большего к меньшему)
        const sorted = [...states].sort((a, b) => {
            const priceA = a.rule.CONDITION?.ORDER_PRICE_FROM || 0;
            const priceB = b.rule.CONDITION?.ORDER_PRICE_FROM || 0;
            return priceB - priceA;
        });

        const bestState = sorted[0]!;
        if (!bestState.rule.BENEFITS || bestState.rule.BENEFITS.length === 0) return null;

        // Берем первый подарок из лучшего правила
        const firstGift = bestState.rule.BENEFITS.find(b => {
            const benefitWithType = b as typeof b & { TYPE: string };
            return benefitWithType.TYPE === 'gift';
        }) as BasketRuleBenefitGift | undefined;

        return firstGift?.UID || firstGift?.PRODUCT_ID || null;
    };

    /**
     * Валидация и автоматический выбор подарков
     */
    const validateAndSelectGifts = () => {
        // Валидация основного подарка
        if (selectedGift.value && !isGiftAvailable(selectedGift.value, false)) {
            // Текущий подарок недоступен, выбираем лучший
            const bestGift = findBestGift(false);
            selectedGift.value = bestGift;

            if (bestGift) {
                // TODO: Показать уведомление о смене подарка
                console.log('[BasketRulesStore] Auto-selected best gift:', bestGift);
            }
        } else if (!selectedGift.value && availableGifts.value.length > 0) {
            // Подарок не выбран, но есть доступные - выбираем лучший
            selectedGift.value = findBestGift(false);
        }

        // Валидация дополнительного подарка
        if (selectedGiftAdditional.value && !isGiftAvailable(selectedGiftAdditional.value, true)) {
            // Текущий дополнительный подарок недоступен, выбираем лучший
            const bestGift = findBestGift(true);
            selectedGiftAdditional.value = bestGift;

            if (bestGift) {
                console.log('[BasketRulesStore] Auto-selected best additional gift:', bestGift);
            }
        } else if (!selectedGiftAdditional.value && availableAdditionalGifts.value.length > 0) {
            // Дополнительный подарок не выбран, но есть доступные - выбираем лучший
            selectedGiftAdditional.value = findBestGift(true);
        }
    };

    /**
     * Выбрать подарок
     */
    const selectGift = (giftId: number | string, isAdditional = false) => {
        if (!isGiftAvailable(giftId, isAdditional)) {
            console.error('[BasketRulesStore] Gift not available:', giftId);
            return;
        }

        if (isAdditional) {
            selectedGiftAdditional.value = giftId;
        } else {
            selectedGift.value = giftId;
        }
    };

    /**
     * Получить товар подарка
     */
    const getGiftProduct = (giftId: number | string): ProductModel | null => {
        // Ищем подарок в доступных
        const allGifts = [...availableGifts.value, ...availableAdditionalGifts.value];
        const gift = allGifts.find(g => g.UID === giftId || g.PRODUCT_ID === giftId);

        if (!gift || !gift.PRODUCT_ID) return null;

        return productsModelsById.value[gift.PRODUCT_ID] || null;
    };

    // ============================================================================
    // DISCOUNT CALCULATION
    // ============================================================================

    /**
     * Получает максимальную скидку из массива скидок
     * @param discounts - Массив скидок для сравнения
     * @returns Скидка с максимальным процентом или null
     */
    const getMaxDiscount = (discounts: DiscountBenefitWithRule[]): DiscountBenefitWithRule | null => {
        if (discounts.length === 0) return null;

        return discounts.reduce((max, current) => {
            const maxPercent = max.DISCOUNT_PERCENT || 0;
            const currentPercent = current.DISCOUNT_PERCENT || 0;
            return currentPercent > maxPercent ? current : max;
        });
    };


    /**
     * Map с расчетами скидок для всех товаров каталога
     * Автоматически пересчитывается при изменении базово активных правил скидок
     *
     * Использует baseActiveDiscountRulesStates (без учета PRODUCT условия)
     * для показа скидок в каталоге до добавления товара в корзину
     *
     * Requirements: 5.1, 5.3, 7.3
     */
    const productsPriceDiscounted = computed(() => {
        return catalogStore.productsModels.reduce<Record<string, ProductDiscountsCalculation>>((map, product) => {
            map[product.ID] = calculateProductDiscountsForCatalogUtil(product, catalogActiveDiscountRules.value)
            return map
        }, {})
    });

    /**
     * Массив с расчетами для всех элементов корзины
     * Автоматически пересчитывается при изменении активных правил или basketItems
     *
     * Совместимость с существующей системой:
     * - Использует тот же формат BasketItemCalculated[]
     * - Обеспечивает обратную совместимость с basketItemsBenefited
     *
     * Requirements: 5.1, 5.2, 5.4, 7.4
     */
    const basketItemsBenefited = computed<BasketItemCalculated[]>(() => {
        const items = basketStore.basketItemsModels;

        return items.map(item => {
            const product = item.product;

            // Обработка случая когда item.product отсутствует
            if (!product) {
                return {
                    model: item,
                    calc: {
                        discountPercent: 0,
                        price: item.fields.PRICE_BASE,
                        priceSource: item.fields.PRICE_BASE,
                        priceTotal: item.fields.PRICE_BASE * item.fields.QUANTITY,
                        priceTotalSource: item.fields.PRICE_BASE * item.fields.QUANTITY
                    }
                };
            }

            // Рассчитываем скидку для товара
            // Используем PRICE_BASE из корзины, а не из каталога
            const discountCalc = calculateProductDiscountsUtil(product, activeDiscountRules.value, {
                basePrice: item.fields.PRICE_BASE,
                round: true
            });

            return {
                model: item,
                calc: {
                    discountPercent: discountCalc.discountPercent,
                    price: discountCalc.price,
                    priceSource: discountCalc.priceSource,
                    priceTotal: Math.ceil(discountCalc.price * item.fields.QUANTITY),
                    priceTotalSource: discountCalc.priceSource * item.fields.QUANTITY,
                    appliedRule: discountCalc.appliedRule,
                    appliedRuleId: discountCalc.appliedRuleId,
                }
            };
        });
    });

    const basketPriceDiscounted = computed(() => {
        return basketItemsBenefited.value.reduce((acc, item) => {
            acc += item.calc.priceTotal;
            return acc;
        }, 0);
    });


    // Автоматическая валидация при изменении доступных подарков
    watch([availableGifts, availableAdditionalGifts], () => {
        validateAndSelectGifts();
    });

    // SCOPE QUERY
    registerScopeQuery(
        STORE_NAME,
        "user",
        {
            basket_rule_list: {
                __fragment: "BasketRuleFields",
            },
        },
        (data: any) => {
            if (data.basket_rule_list) {
                allRules.value = data.basket_rule_list;
            }
        }
    );

    /**
     * Получить товары из активных правил корзины
     *
     * Аналогично getProducts из modificators store, но для basket rules.
     * Собирает товары из BENEFITS_PRODUCT_ID_ENTITIES правил с учетом фильтров.
     *
     * @param filters - Фильтры для отбора товаров
     * @returns Массив товаров ProductModel
     *
     * Критерии отбора:
     * - ruleActive: только из активных правил (по умолчанию true)
     * - benefitTypes: типы льгот ['discount', 'gift']
     * - includeInactiveByPrice: включать правила неактивные только по цене
     * - inBasket: товар в корзине (true) или не в корзине (false)
     */
    function getProducts(filters: {
        ruleActive?: boolean;           // Только активные правила
        benefitTypes?: string[];        // Типы льгот ['discount', 'gift']
        includeInactiveByPrice?: boolean; // Включать неактивные по цене
        inBasket?: boolean;             // Товар в корзине (true) или не в корзине (false)
    } = {}): ProductModel[] {
        const {
            ruleActive = true,
            benefitTypes,
            includeInactiveByPrice = false,
            inBasket,
        } = filters;

        const result: ProductModel[] = [];
        const addedProductIds = new Set<number>();

        // Определяем список состояний правил
        let states: BasketRuleState[] = [];

        if (ruleActive) {
            states = activeRules.value;

            // Добавляем неактивные по цене если нужно
            if (includeInactiveByPrice) {
                for (const rule of allRules.value) {
                    const state = rulesStateMap.value[rule.ID];
                    if (state && !state.active && state.priceRemaining > 0) {
                        states.push(state);
                    }
                }
            }
        } else {
            // Преобразуем все правила в состояния
            states = Object.values(rulesStateMap.value);
        }

        // Собираем товары из PROPERTIES.BENEFITS_PRODUCT_ID_ENTITIES
        for (const state of states) {
            if (!state.rule.PROPERTIES?.BENEFITS_PRODUCT_ID_ENTITIES || state.rule.PROPERTIES.BENEFITS_PRODUCT_ID_ENTITIES.length === 0) {
                continue;
            }

            // Фильтруем по типам льгот если указаны
            if (benefitTypes && benefitTypes.length > 0 && state.rule.BENEFITS) {
                // Проверяем есть ли хотя бы один benefit нужного типа
                const hasMatchingBenefit = state.rule.BENEFITS.some(benefit =>
                    benefit && benefitTypes.includes(benefit.TYPE)
                );
                if (!hasMatchingBenefit) continue;
            }

            // Добавляем товары из PROPERTIES.BENEFITS_PRODUCT_ID_ENTITIES
            for (const productElement of state.rule.PROPERTIES.BENEFITS_PRODUCT_ID_ENTITIES) {
                const productId = productElement.ID;

                // Пропускаем дубликаты
                if (addedProductIds.has(productId)) continue;

                const product = productsModelsById.value[productId];
                if (!product) continue;

                // Фильтруем по наличию в корзине
                if (inBasket !== undefined) {
                    const productInBasket = basketStore.basketItems.some(
                        item => item.fields.PRODUCT_ID === productId
                    );
                    if (inBasket && !productInBasket) continue;
                    if (!inBasket && productInBasket) continue;
                }

                result.push(product);
                addedProductIds.add(productId);
            }
        }

        return result;
    }

    // ============================================================================
    // BENEFIT TYPE MANAGEMENT
    // ============================================================================


    /**
     * Устанавливает тип benefit
     *
     * @param type - тип benefit для установки
     */
    const setBenefitType = (type: BenefitTypeEnum) => {

        if (type !== 'coupon' && vorderCoupon.hasCoupon) {
            vorderCoupon.removeCouponLocal()
        }

        if (type === 'prize') {
            if (vorderPrize.prize) {
                vorderPrize.selectPrize(vorderPrize.prize)
                attrs.value.BENEFIT_TYPE = type;
            }
        } else if (type === 'coupon') {
            if (vorderCoupon.coupon) {
                vorderCoupon.selectCoupon(vorderCoupon.coupon)
                attrs.value.BENEFIT_TYPE = type;
            }
        } else {
            attrs.value.BENEFIT_TYPE = type;
        }
    };

    const isAvailableBenefitType = (type: BenefitTypeEnum): boolean => {
        switch (type) {
            case 'bonus':
                return loalityStore.bonusesAvailable > 0
            case 'discount':
                return !!discountBenefitsStandaloneBest.value;
            case 'prize':
                return vorderPrize.hasPrize
            case 'coupon':
                return vorderCoupon.hasCoupon
            case 'gift':
                return false
            default:
                return false;
        }
    };

    /**
     * Проверяет возможность установки типа benefit
     *
     * @param type - тип benefit для проверки
     * @returns true если тип может быть установлен, false иначе
     *
     * Проверяемые условия:
     * - bonus: наличие доступных бонусов (bonusesAvailable > 0) И bonuses > 0
     * - discount: наличие активной скидки
     * - prize: наличие приза пользователя (userPrizeElement) И PRIZE_ID установлен
     * - coupon/promocode: наличие активного промокода (coupon) И COUPON_ID установлен
     */
    const canSetBenefitType = (type: BenefitTypeEnum): boolean => {
        switch (type) {
            case 'bonus':
                return loalityStore.bonusesAvailable > 0 && loalityStore.bonuses > 0
            case 'discount':
                return !!discountBenefitsStandaloneBest.value;
            case 'prize':
                return vorderPrize.hasPrize
            case 'coupon':
                return vorderCoupon.hasCoupon
            default:
                return false;
        }
    };

    /**
     * Вычисляет оптимальный тип benefit на основе текущего состояния
     *
     * Приоритет:
     * 1. bonus (если есть доступные бонусы)
     * 2. coupon (если есть активный промокод)
     * 3. discount (если есть активная скидка)
     * 4. prize (если есть приз пользователя)
     *
     * @returns Оптимальный тип benefit или undefined
     */
    const calcBenefitType = (): BenefitTypeEnum | undefined => {

        if (vorderPrize.hasPrize) {
            return 'prize';
        }

        if (vorderCoupon.hasCoupon) {
            return 'coupon';
        }

        if (loalityStore.bonusesAvailable > 0 && bonuses.value > 0) {
            return 'bonus';
        }

        // Приоритет 3: Скидка из basket rules
        if (!!discountBenefitsStandaloneBest.value) {
            return 'discount';
        }

        return undefined;
    };

    /**
     * Гарантирует корректность установленного типа benefit
     * Если текущий тип недоступен - вычисляет оптимальный
     *
     * @param type - желаемый тип benefit (опционально)
     */
    const ensureBenefitType = (type?: BenefitTypeEnum | null) => {
        type = type || attrs.value.BENEFIT_TYPE || null;

        let typeAvailable: BenefitTypeEnum | null | undefined = type

        // Если тип не указан или недоступен - вычисляем оптимальный
        if (!type || !canSetBenefitType(type)) {
            typeAvailable = calcBenefitType()
        }

        console.log('ensureBenefitType', {
            'currentType': type,
            'availableType': typeAvailable,
            'isChanged': typeAvailable !== type,
            'attrBenefitType': attrs.value.BENEFIT_TYPE
        })

        if (typeAvailable && attrs.value.BENEFIT_TYPE !== typeAvailable) {
            setBenefitType(typeAvailable)
            console.log('type changed')
        }
    };


    return {
        // Состояние
        allRules: computed(() => allRules.value),
        selectedGift: computed(() => selectedGift.value),
        selectedGiftAdditional: computed(() => selectedGiftAdditional.value),

        // Карты состояний
        rulesStateMap: computed(() => rulesStateMap.value),

        // Активные правила (полное состояние)
        activeRulesStates: activeRules,
        baseActiveRulesStates: baseActiveRules,
        activeDiscountRulesStates: activeDiscountRules,
        baseActiveDiscountRulesStates: baseActiveDiscountRules,
        activeGiftRulesStates: activeGiftRules,
        activeRegularGiftRulesStates: activeRegularGiftRules,
        activeAdditionalGiftRulesStates: activeAdditionalGiftRules,
        activeSpecialRulesStates: activeSpecialRules,

        // Доступные льготы
        availableDiscounts,
        availableGifts,
        availableAdditionalGifts,

        // Special правила
        specialProducts,
        isProductAvailableBySpecial,
        getSpecialRulesStatesForProduct,

        // Методы выбора подарков
        selectGift,
        isGiftAvailable,
        getGiftProduct,
        validateAndSelectGifts,

        // Функции пересчета для оркестратора
        recalculateActiveBasketRules,
        debouncedRecalculateActiveBasketRules,

        // Расчет скидок (Requirements 5.1, 5.2, 5.3, 7.3, 7.4)
        productsPriceDiscounted,
        basketItemsBenefited,

        // Получение товаров
        getProducts,

        // Управление типом льготы (BENEFIT_TYPE)
        isAvailableBenefitType,
        canSetBenefitType,
        calcBenefitType,
        ensureBenefitType,
        setBenefitType,

        discountBenefitsStandalone,
        discountBenefitsStandaloneBest,
        pickupDiscount,
        deliveryDiscount,
        deliveryPriceDiscounted,
        basketPriceDiscounted,
        promoBenefits
    };
});
